<script setup lang="ts">
  import FormHeading from '@/components/form/FormHeading.vue'
  import ExtraJourneyOptions from '@/components/idf-modal/ExtraJourneyOptions.vue'
  import JourneyCarrier from '@/components/idf-modal/JourneyCarrier.vue'
  import JourneyGrondbank from '@/components/idf-modal/JourneyGrondbank.vue'
  import JourneyHandler from '@/components/idf-modal/JourneyHandler.vue'
  import JourneyIhm from '@/components/idf-modal/JourneyIhm.vue'
  import JourneyLastCreated from '@/components/idf-modal/JourneyLastCreated.vue'
  import JourneyProductModal from '@/components/idf-modal/JourneyProductModal.vue'
  import JourneyValidation from '@/components/idf-modal/JourneyValidation.vue'
  import { useJourneyValidation } from '@/composables/validation/use-journey-validation'
  import type { EditIdfJourney } from '@/model/edit-idf-journey.model'
  import { useJourneyTemplateStore } from '@/store/journey-template.store'
  import { useLegacyAppStore } from '@/store/legacy-app.store'
  import { useLegacyProjectStore } from '@/store/legacy-project.store'
  import { useProductStore } from '@/store/product.store'
  import { useRdCodeStore } from '@/store/rd-code.store'
  import { type JourneyProduct } from '@/types/idf-journey-product'
  import { COLUMN_ORDER, EXCLUDED_PRODUCTS_KEYS, LABEL_OVERRIDES } from '@/types/product'
  import * as modules from '@/utils/modules'
  import { createFlatBaseColumns, setWeight } from '@/utils/table'
  import { scrollToTopError, trim } from '@/utils/validation'
  import {
    FormInput,
    Modal,
    SwitchButton,
    Table,
    UiButton,
    useToastStore,
    type Column,
  } from '@logistiek-zonder-papier/lzp-library'
  import { AxiosError } from 'axios'
  import { storeToRefs } from 'pinia'
  import { computed, nextTick, onMounted, ref, watch, type Ref } from 'vue'
  import { useRoute } from 'vue-router'

  const { error } = useToastStore()

  const productStore = useProductStore()
  const rdCodeStore = useRdCodeStore()

  const projectStore = useLegacyProjectStore()
  const { projectStromen } = storeToRefs(projectStore)

  const appStore = useLegacyAppStore()
  const { journeyTemplateFieldConfigs, subModuleIds } = storeToRefs(appStore)

  const journeyTemplateStore = useJourneyTemplateStore()
  const { journeyTemplate, journeyTemplateGrondbank, journeyProducts } =
    storeToRefs(journeyTemplateStore)

  const { showCreateButton = 'false' } = defineProps<{ showCreateButton?: string }>()
  const showCreateButtonBool = computed(() => showCreateButton === 'true')

  const route = useRoute()

  const journeyProductColumns: Ref<Column[]> = ref([])
  const show = ref(false)
  const isEdit = ref(false)
  const grondbankToggle = ref(false)
  const repeatToggle = ref(false)
  const grondbankCheckbox = ref(false)
  const repeatCheckbox = ref(false)
  const submitAttempted = ref(false)

  const { errors, validate } = useJourneyValidation(
    journeyTemplate,
    journeyTemplateGrondbank,
    journeyTemplateFieldConfigs,
    grondbankToggle
  )
  const visibleErrors = computed(() => (submitAttempted.value ? errors.value : {}))
  const warning = ref<string | null>(null)
  const warningGrondbank = ref<string | null>(null)

  onMounted(async () => {
    const uuid = route.params['uuid']
    if (typeof uuid === 'string') {
      await productStore.fetchProjectProducts(uuid)
      await projectStore.fetchProjectStromen(uuid)
      await rdCodeStore.fetchRdCodes()
    }
  })

  function submit(close: () => void) {
    if (isEdit.value) {
      onEdit(close)
    } else {
      onSubmit(close)
    }
  }

  async function onEdit(close: () => void) {
    submitAttempted.value = true
    if (!validate()) {
      scrollToTopError()
      return
    }
    await journeyTemplateStore.editIdfJourneyTemplate()
    // Update the organization datatable organisaties.js listens to this event and triggers a reload of the datatable
    window.dispatchEvent(
      new CustomEvent('reload-dataTable', {
        detail: {
          success: true,
          message: 'De stroom is succesvol bijgewerkt.',
        },
      })
    )
    isEdit.value = false
    onClose(close)
  }

  async function onSubmit(close: () => void) {
    submitAttempted.value = true
    if (!validate()) {
      scrollToTopError()
      return
    }

    try {
      const uuid = route.params['uuid']
      if (typeof uuid === 'string') {
        journeyTemplateStore.updateJourneyTemplate({ project: { projectUUID: uuid } })
      }
      await journeyTemplateStore.createIdfJourneyTemplate()
      window.dispatchEvent(new Event('reload-vue'))
      // Update the organization datatable organisaties.js listens to this event and triggers a reload of the datatable
      window.dispatchEvent(
        new CustomEvent('reload-dataTable', {
          detail: {
            success: true,
            message: 'De stroom is succesvol aangemaakt.',
          },
        })
      )
      onClose(close)
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        warning.value = 'Kenmerk is al ingebruik'
        scrollToTopError()
      }
      throw error
    }
  }

  function createJourneyProductColumns(journeyProducts: JourneyProduct[]) {
    if (journeyProducts.length > 0)
      journeyProductColumns.value = [
        ...createFlatBaseColumns(journeyProducts, EXCLUDED_PRODUCTS_KEYS, {
          columnOrder: COLUMN_ORDER,
          labelOverrides: LABEL_OVERRIDES,
        }),
        { key: 'action', label: 'actie' },
      ]

    return journeyProductColumns.value
  }

  function onClose(close: () => void) {
    journeyTemplateStore.resetJourneyTemplate()
    submitAttempted.value = false
    journeyProducts.value = []
    journeyTemplateGrondbank.value = null
    grondbankToggle.value = false
    if (grondbankCheckbox.value) grondbankCheckbox.value = false
    close()
  }

  function consignmentNameCheck() {
    const exists = projectStromen.value.find(
      prod => trim(prod.stroomName) === trim(journeyTemplate.value.stroomName)
    )

    if (exists && !isEdit.value) {
      warning.value = 'Kenmerk is al ingebruik'
    } else {
      warning.value = null
    }
  }

  function consignmentGrondbankNameCheck() {
    const exists = projectStromen.value.find(
      prod => trim(prod.stroomName) === trim(journeyTemplateGrondbank.value?.stroomNameGrondbank)
    )

    if (exists && !isEdit.value) {
      warningGrondbank.value = 'Kenmerk is al ingebruik'
    } else {
      warningGrondbank.value = null
    }
  }

  watch(
    () => journeyTemplate.value.stroomName,
    () => {
      consignmentNameCheck()
    }
  )

  watch(
    () => journeyTemplateGrondbank.value?.stroomNameGrondbank,
    () => {
      consignmentGrondbankNameCheck()
    }
  )

  watch(
    () => !isEdit.value && journeyTemplate.value.stroomProducent,
    () => {
      journeyTemplateStore.updateJourneyTemplate({ verzendAdres: null })
    }
  )

  watch(
    () => !isEdit.value && journeyTemplate.value.stroomVerwerker,
    () => {
      journeyTemplateStore.updateJourneyTemplate({ bestemmingAdres: null })
    }
  )

  function setIdfJourneyTemplate(editIdfJourney: EditIdfJourney, isEditMode = false) {
    isEdit.value = isEditMode
    show.value = true

    const parsedTemplate = journeyTemplateStore.getEditIdfJourneyTemplate(editIdfJourney)
    journeyTemplate.value = parsedTemplate

    nextTick(() => {
      journeyTemplate.value = parsedTemplate
    })
  }

  const showGrondbankSwitch = computed(
    () =>
      subModuleIds.value.includes(modules.ACTIVE_PROJECTS_NO_PRODUCTS_GRONDBANK_SUBMODULE_ID) ||
      subModuleIds.value.includes(modules.ACTIVE_PROJECTS_NO_VERVOERDER_GRONDBANK_SUBMODULE_ID)
  )

  function toggleGrondbank() {
    grondbankToggle.value = !grondbankToggle.value
    repeatCheckbox.value = false

    if (grondbankToggle.value) {
      journeyTemplateGrondbank.value = {
        stroomNameGrondbank: journeyTemplate.value.stroomName ?? null,
        drieDeligCode: null,
        repeating: 'off',
        partijNummer: null,
      }
    } else {
      journeyTemplate.value.stroomName =
        journeyTemplateGrondbank.value?.stroomNameGrondbank ?? journeyTemplate.value.stroomName
      journeyTemplateGrondbank.value = null
    }
  }

  function toggleRepeat() {
    repeatToggle.value = !repeatToggle.value

    if (journeyTemplateGrondbank.value !== null) {
      journeyTemplateGrondbank.value.repeating = repeatToggle.value ? 'on' : 'off'
    }
  }

  async function fetchCodes() {
    if (
      journeyTemplateGrondbank.value?.stroomNameGrondbank === null ||
      journeyTemplateGrondbank.value!.stroomNameGrondbank.trim().length < 5
    ) {
      error('Documentnummer moet minimaal 5 karakters lang zijn', 2500)
    } else {
      try {
        await journeyTemplateStore.fetchGrondbankCodes(
          journeyTemplateGrondbank.value!.stroomNameGrondbank
        )
      } catch (err) {
        error('Grondtransportmelding is niet bekend bij Grondbank', 2500)
        throw err
      }
    }
  }

  const requiredProducent = computed(
    () => !subModuleIds.value.includes(modules.ACTIVE_PROJECTS_SCHIP_SUBMODULE_ID)
  )

  function removeProduct(uuid: string) {
    if (journeyProducts.value.length > 0) {
      journeyProducts.value = [...journeyProducts.value.filter(prod => prod.uuid !== uuid)]
      journeyTemplate.value.materialen = [...journeyProducts.value]
    }
  }

  defineExpose({
    setIdfJourneyTemplate,
  })
</script>

<template>
  <UiButton
    v-if="showCreateButtonBool"
    label="Nieuw indentificatieformulier"
    @click="show = !show"
    icon="plus"
    buttonVariant="legacy-portal"
  />
  <Modal v-model:show="show">
    <SwitchButton
      v-if="!isEdit && showGrondbankSwitch"
      label="Grondbank koppeling"
      v-model="grondbankCheckbox"
      @change="toggleGrondbank()"
    />

    <JourneyGrondbank
      v-if="grondbankToggle && journeyTemplateGrondbank !== null"
      v-model="journeyTemplateGrondbank"
      :visible-errors="visibleErrors"
      :warning-grondbank="warningGrondbank"
      :is-edit="isEdit"
      :repeat="repeatCheckbox"
      @fetchCodes="fetchCodes"
      @toggleRepeat="toggleRepeat"
    />

    <template #header> Nieuw Identificatieformulier toevoegen </template>
    <div v-if="!grondbankToggle">
      <FormHeading label="Kenmerk" />
      <FormInput
        v-model="journeyTemplate.stroomName"
        placeholder="Kenmerkformulier"
        label="Kenmerkformulier"
        :error="visibleErrors.stroomName"
        :data-validation-error="!!visibleErrors.stroomName || warning !== null"
        :warning="isEdit ? null : warning"
      />
      <JourneyLastCreated />
    </div>

    <JourneyHandler
      heading-label="Identificatie van de afvalstoffenproducent"
      location-label="Adres van verzending"
      locationType="origin"
      v-model:handler-organization="journeyTemplate.stroomProducent"
      v-model:handler-address="journeyTemplate.verzendAdres"
      :error="visibleErrors"
      organization-error-key="stroomProducent"
      address-error-key="verzendAdres"
      :required="requiredProducent"
    />

    <JourneyIhm
      heading-label="Identificatie van de inzamelaar, afvalstoffenhandelaar of -makelaar"
      v-model:ihm-organization="journeyTemplate.stroomIhm"
    />

    <JourneyCarrier :error="visibleErrors" carrier-error-key="stroomVervoerder" />

    <JourneyHandler
      heading-label="Identificatie van de verwerker"
      location-label="Verwerkerslocatie"
      locationType="destination"
      v-model:handler-organization="journeyTemplate.stroomVerwerker"
      v-model:handler-address="journeyTemplate.bestemmingAdres"
      :error="visibleErrors"
      organization-error-key="stroomVerwerker"
      address-error-key="bestemmingAdres"
    />

    <FormHeading label="Identificatie van de afvalstoffen" />
    <JourneyProductModal />
    <Table :rows="journeyProducts" :columns="createJourneyProductColumns(journeyProducts)">
      <template #cell-naam="{ row }">
        {{ row['omschrijving'] || row['naam'] }}
      </template>
      <template #cell-gewicht="{ row }">
        {{ setWeight(row['gewicht'], row['eenheid']) }}
      </template>
      <template #cell-euralCode="{ row }">
        <div>{{ row['euralCode']?.code }}</div>
      </template>
      <template #cell-rdCode="{ row }">
        {{ row['rdCode']?.code }}
      </template>
      <template #cell-action="{ row }">
        <UiButton
          @click="removeProduct(row['uuid'])"
          variant="negative"
          icon="cross"
          size="small"
        />
      </template>
    </Table>
    <JourneyValidation :message="visibleErrors.materialen" />

    <FormHeading label="Instructies transport (indien van toepassing)" />
    <FormInput
      label="Transport instructies"
      v-model="journeyTemplate.transportInstructies"
      placeholder="Voer instructies in…"
      textarea
      optional
    />

    <ExtraJourneyOptions :error="visibleErrors" />

    <template #footer="{ close }">
      <div class="save-row">
        <UiButton @click.prevent="onClose(close)" variant="default" label="Sluiten" />
        <UiButton @click.prevent="submit(close)" variant="prominent" label="Opslaan" />
      </div>
    </template>
  </Modal>
</template>

<style scoped>
  .section-container {
    margin: 0 2rem;
  }

  .save-row {
    display: flex;
    flex-direction: row;
    justify-content: end;
    gap: var(--spacing-s);
  }
</style>
